import { Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";
import CheckIcon from "@heroicons/react/20/solid/CheckIcon";
import ChevronDownIcon from "@heroicons/react/20/solid/ChevronDownIcon";

import cx from "@hsl/core/utils/cx";

import { Translate } from "../../hooks/useTranslation";
import CustomScroller from "../CustomScroller";

interface OptionProp<V> {
    key: string;
    label: string;
    value: V;
    disabled?: boolean;
}

export type Props<V> = {
    buttonClassName?: string;
    selectedClassName?: string;
    buttonContainerClassName?: string;
    className?: string;
    label?: string;
    options: OptionProp<V>[];
    optionClassName?: string;
    listClassName?: string;
    scrollerClassName?: string;
    selectedOptionClassName?: string;
    selectedOptionTick?: boolean;
    noArrow?: boolean;
    onFocus?(): void;
} & (
    | {
          type?: "single";
          value?: V;
          onChange?(value: V): void;
      }
    | {
          type: "multiple";
          value?: V[];
          onChange?(value: V[]): void;
      }
);

const Select = <V,>({
    buttonClassName,
    selectedClassName,
    buttonContainerClassName,
    className,
    label,
    options,
    optionClassName,
    listClassName,
    scrollerClassName,
    selectedOptionClassName = "font-medium",
    selectedOptionTick = true,
    value,
    type = "single",
    noArrow,
    onChange,
    onFocus,
}: Props<V>) => {
    const selectedOption = value
        ? Array.isArray(value)
            ? options.filter((o) => value.includes(o.value))
            : options.find((o) => o.value === value)
        : options[0];

    const activeLabel = () =>
        Array.isArray(selectedOption)
            ? selectedOption.map((s) => (s.value ? s.label : "")).join(", ")
            : selectedOption?.value
              ? selectedOption?.label
              : "";

    const handleChange = (value: OptionProp<V> | OptionProp<V>[]) => {
        if (onChange) {
            onChange(
                // @ts-expect-error I give up
                Array.isArray(value) ? value.map((v) => v.value) : value.value,
            );
        }
    };

    return (
        <div className={className}>
            <Listbox
                value={selectedOption}
                onChange={handleChange}
                multiple={type === "multiple"}
            >
                {({ open }) => (
                    <div className={cx("relative", buttonContainerClassName)}>
                        <Listbox.Button
                            className={cx(
                                "text-left sm:text-sm", //should this sm: be here?
                                "relative flex items-center",
                                "cursor-pointer",
                                "focus:outline-none focus-visible:border-indigo-500",
                                "focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-300",
                                buttonClassName,
                            )}
                            onFocus={onFocus}
                        >
                            <span className="block truncate">
                                {label && `${label} `}
                                <span className={selectedClassName}>
                                    <Translate>{activeLabel()}</Translate>
                                </span>
                            </span>
                            {!noArrow && (
                                <ChevronDownIcon
                                    className={cx(
                                        "ml-1 h-4 w-4 transition",
                                        open && "rotate-180 transform",
                                        selectedClassName,
                                    )}
                                />
                            )}
                        </Listbox.Button>
                        <Transition
                            as={Fragment}
                            leave="transition duration-75 ease-out"
                            leaveFrom="transform scale-100 opacity-100"
                            leaveTo="transform scale-95 opacity-0"
                        >
                            <CustomScroller
                                className={cx(
                                    "absolute z-50 max-h-60 shadow-md",
                                    scrollerClassName,
                                )}
                            >
                                <Listbox.Options
                                    className={cx(
                                        "min-w-[160px] rounded-sm bg-white py-px text-xs ring-1 ring-black ring-opacity-5 focus:outline-none",
                                        listClassName,
                                    )}
                                >
                                    {options.map((item) => (
                                        <Listbox.Option
                                            key={item.key}
                                            className={cx(
                                                `relative cursor-default select-none py-1.5 pl-7 pr-4 hover:bg-gray-50 hover:font-medium`,
                                                optionClassName,
                                            )}
                                            value={item}
                                        >
                                            {({ selected }) => (
                                                <>
                                                    <span
                                                        className={cx(
                                                            "block truncate",
                                                            selected &&
                                                                selectedOptionClassName,
                                                        )}
                                                    >
                                                        <Translate>
                                                            {item.label}
                                                        </Translate>
                                                    </span>
                                                    {selected &&
                                                        selectedOptionTick && (
                                                            <span className="absolute inset-y-0 left-0 flex items-center pl-1.5">
                                                                <CheckIcon
                                                                    className="h-4 w-4"
                                                                    aria-hidden="true"
                                                                />
                                                            </span>
                                                        )}
                                                </>
                                            )}
                                        </Listbox.Option>
                                    ))}
                                </Listbox.Options>
                            </CustomScroller>
                        </Transition>
                    </div>
                )}
            </Listbox>
        </div>
    );
};

export default Select;
